import React from "react";
import {observer} from "mobx-react-lite";
import {
    Box,
    Button,
    Chip,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {Add, Search} from "@mui/icons-material";
import {useStores} from "~/stores";
import {useFilterDefinitions} from "./useFilterDefinitions";

/**
 * The filter bar of the Spend Landscape page (Mithra 3.0 design).
 *
 * Changes are collected and loaded together with Apply; Clear all goes back to the defaults straight away.
 */
export const SpendLandscapeFilterBar: React.FC = observer(() => {
    const {bagStore} = useStores();
    const filterStore = bagStore.advanced.filterStore;
    const definitions = useFilterDefinitions();
    const [added, setAdded] = React.useState<string[]>([]);
    const [addAnchor, setAddAnchor] = React.useState<HTMLElement | null>(null);
    const [search, setSearch] = React.useState('');

    if (!bagStore.advanced.ready) return null;

    const editing = filterStore.editingFilters;
    const shown = definitions.filter(d => d.primary || added.includes(d.id) || d.isActive(editing));
    const addable = definitions
        .filter(d => !shown.includes(d))
        .filter(d => !search || d.label.toLowerCase().includes(search.toLowerCase()));

    return <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} className="spend-landscape-filter-bar">
        {shown.map(d => <Box key={d.id}>{d.render()}</Box>)}

        {definitions.length > shown.length && <>
            <Button
                className="spend-landscape-add-filter"
                variant="outlined"
                startIcon={<Add/>}
                onClick={e => {
                    setSearch('');
                    setAddAnchor(e.currentTarget);
                }}>
                Add filter
            </Button>
            <Popover
                open={Boolean(addAnchor)}
                anchorEl={addAnchor}
                onClose={() => setAddAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}>
                <Box sx={{width: '20em', p: 1}}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Search filters"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        slotProps={{input: {startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}}/>
                    <List dense>
                        {addable.map(d => <ListItemButton
                            key={d.id}
                            onClick={() => {
                                setAdded([...added, d.id]);
                                setAddAnchor(null);
                            }}>
                            <ListItemText primary={d.label}/>
                            <Chip label="Property" size="small" variant="outlined"/>
                        </ListItemButton>)}
                        {addable.length === 0 &&
                            <Typography variant="body2" color="text.secondary" sx={{p: 1}}>No filters found</Typography>}
                    </List>
                </Box>
            </Popover>
        </>}

        <Button
            variant="text"
            disabled={filterStore.clearDisabled}
            onClick={() => {
                setAdded([]);
                filterStore.reset();
            }}>
            Clear all
        </Button>
        <Button
            variant="contained"
            disableElevation
            disabled={!filterStore.hasStagedChanges}
            onClick={() => filterStore.applyStaged()}>
            Apply
        </Button>
        {filterStore.hasStagedChanges &&
            <Typography variant="caption" color="text.secondary">Not applied yet</Typography>}
    </Stack>;
});
